import {
	Table,
	Icons,
	Stack,
	Text,
	Money,
	Box,
	Dropdown,
	joinAttributes,
	RelativeTime,
	AsyncSelectProps,
	Inline,
	AsyncSelect,
	Badge,
	Tooltip,
} from "@sembark-travel/ui-base"
import {
	CursorListView,
	Search,
	TSearchParams,
	useSearch,
} from "@sembark-travel/ui-list"
import { Link, useLocationQuery } from "@sembark-travel/ui-router"
import { useEffect, useRef, useState } from "react"
import config from "../config"
import { PhoneNumber, Email } from "../Contacts"
import { generatePath } from "../router-utils"
import { TTourist, TTouristGroup } from "./store"
import { ILocation, SelectLocations } from "../Locations"
import {
	dateToQuery,
	dateToUTCString,
	endOf,
	parseDateFromQuery,
	startOf,
} from "@sembark-travel/datetime-utils"
import {
	DateRangePickerField,
	isTruthy,
	SelectField,
} from "@sembark-travel/ui-form"
import { TemporaryStorage } from "../DownloadManager"
import { ISimpleListResponse, useXHR } from "@sembark-travel/xhr"
import { AddTouristDocumentDialog, ITouristOrGroupOption } from "./NewItem"
import { IDocType } from "../DocTypes"
import { EditTouristProfileDialog as EditAdditionalInfoDialog } from "../TouristProfiles"
import { AddTouristProfileDialog as AddAdditionalInfoDialog } from "./AddTouristProfile"

type TFilters = TSearchParams & {
	created_after?: Date
	created_before?: Date
	locations?: Array<ILocation>
}

type TFiltersInQuery = TSearchParams & {
	ca?: string
	cb?: string
	locations?: Array<string>
}

function filtersToQuery(filters: TFilters): TFiltersInQuery {
	const { q, page, created_after, created_before, locations } = filters
	const query: TFiltersInQuery = {}
	if (page) {
		query.page = page
	}
	if (q) {
		query.q = q
	}
	if (created_after) {
		query.ca = dateToQuery(created_after)
	}
	if (created_before) {
		query.cb = dateToQuery(created_before)
	}
	if (locations?.length) {
		query.locations = locations.map((l) => `${l.id}__${l.full_name}`)
	}
	return query
}

function queryToFilters(query: TFiltersInQuery): TFilters {
	const { q, page, ca, cb, locations } = query
	const filters: TFilters = {}
	if (page) {
		filters.page = page
	}
	if (q) {
		filters.q = q
	}
	if (ca) {
		filters.created_after = parseDateFromQuery(ca)
	}
	if (cb) {
		filters.created_before = parseDateFromQuery(cb)
	}
	if (locations?.length) {
		filters.locations = locations.map((l) => {
			const [id, ...name] = l.split("__")
			return {
				id,
				full_name: name.join("__"),
			} as never as ILocation
		})
	}
	return filters
}

function filtersToRequest({
	created_after,
	created_before,
	locations,
	...params
}: TFilters) {
	return {
		...params,
		created_after: created_after
			? dateToUTCString(startOf(created_after, "day"))
			: null,
		created_before: created_before
			? dateToUTCString(endOf(created_before, "day"))
			: null,
		locations: locations?.length ? locations.map((l) => l.id) : [],
	}
}

export function TouristsList() {
	const [query, setQuery] = useLocationQuery<TFilters, TFiltersInQuery>({
		toQuery: filtersToQuery,
		fromQuery: queryToFilters,
	})
	const [params, setParams] = useSearch<TFilters>(query)
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	return (
		<Search
			initialParams={params}
			title={"All Tourists"}
			onSearch={(params) => setParams({ ...params, cursor: null })}
			Filters={Filters}
			actions={
				<Dropdown alignRight="sm">
					<Dropdown.ToggleButton>
						<Icons.DotsVertical />
					</Dropdown.ToggleButton>
					<Dropdown.Menu>
						<TemporaryStorage
							initUrl="/tourists/download"
							initParams={{
								...filtersToRequest(params),
								timezone_offset: config.timezoneOffset,
							}}
						>
							{({ message, generate, url, reset }) =>
								url ? (
									<Dropdown.MenuItem
										as="a"
										href={url}
										target="_blank"
										download
										onClick={() => reset()}
									>
										{message}
									</Dropdown.MenuItem>
								) : (
									<Dropdown.MenuItem
										onClick={(e: React.MouseEvent) => {
											e.preventDefault()
											generate()
										}}
									>
										{message || (
											<>
												<Icons.DocumentDownload /> Download as CSV/Excel
											</>
										)}
									</Dropdown.MenuItem>
								)
							}
						</TemporaryStorage>
					</Dropdown.Menu>
				</Dropdown>
			}
		>
			<CursorListView<TTourist, TFilters>
				pageKey="tourists-list"
				params={params}
				fetch={(xhr, params) =>
					xhr
						.get(`/tourists`, {
							params: {
								...filtersToRequest(params),
								include: "latest_given_quote,address,trip,profile",
							},
						})
						.then((resp) => resp.data)
				}
				onCursorChange={(cursor) => setParams({ ...params, cursor })}
			>
				{({ items: tourists, refresh }) => (
					<Table
						bordered
						responsive
						striped
						headers={["Name", "Contact", "Destinations", "Quotation", ""]}
						rows={tourists.map((tourist) => {
							const {
								trip_id,
								type,
								name,
								phone_numbers,
								email,
								address,
								created_at,
								trip,
								profile,
							} = tourist
							return [
								<Link
									color="accent"
									fontWeight="semibold"
									to={generatePath("/trips/:tripId", {
										tripId: trip_id.toString(),
									})}
								>
									<Stack>
										<Inline gap="2">
											<Text>{name}</Text>
											{profile && (
												<Link
													fontWeight="semibold"
													to={generatePath(
														"/tourist-profiles/:touristProfileId",
														{
															touristProfileId: profile.id.toString(),
														}
													)}
												>
													<Box
														size="6"
														width="6"
														height="6"
														display="flex"
														justifyContent="center"
														alignItems="center"
														padding="1"
														backgroundColor="accent"
														borderColor="accent"
														borderWidth="1"
														borderRadius="md"
													>
														<Icons.User color="accent" size="20" />
													</Box>
												</Link>
											)}
										</Inline>
										<Text fontSize="sm">
											{joinAttributes(
												`# ${trip_id}`,
												address ? address.location.name : "",
												<RelativeTime timestamp={created_at} />
											)}
										</Text>
									</Stack>
								</Link>,
								<Stack gap="px" style={{ minWidth: "180px" }}>
									<Box>
										<PhoneNumber value={phone_numbers} />
									</Box>
									{email ? (
										<Box>
											<Email value={email} />
										</Box>
									) : null}
								</Stack>,
								<Box>
									{trip?.destinations?.map((d) => d.name).join(", ") || null}
								</Box>,
								<Stack gap="px">
									{trip?.latest_given_quote ? (
										<Box>
											<Money
												amount={trip.latest_given_quote.given_price}
												currency={trip.latest_given_quote.given_currency}
											/>
										</Box>
									) : null}
									{trip?.current_status ? (
										<Text fontSize="sm" color="muted">
											{trip.current_status}
										</Text>
									) : null}
								</Stack>,
								type === "adult" ? (
									<Box>
										<Dropdown alignRight>
											<Dropdown.ToggleButton level="tertiary" size="sm">
												<Icons.DotsVertical />
											</Dropdown.ToggleButton>
											<Dropdown.Menu>
												{profile ? (
													<EditAdditionalInfoDialog
														touristProfile={profile}
														hideBasicInfo
														onSuccess={() => {
															refresh()
														}}
													>
														{({ show }) => (
															<Dropdown.MenuItem onClick={show}>
																<Icons.Pencil /> Edit Additional Info
															</Dropdown.MenuItem>
														)}
													</EditAdditionalInfoDialog>
												) : (
													<AddAdditionalInfoDialog
														tourist={tourist}
														onSuccess={() => {
															refresh()
														}}
													>
														{({ show }) => (
															<Dropdown.MenuItem onClick={show}>
																<Icons.Plus /> Add Additional Info
															</Dropdown.MenuItem>
														)}
													</AddAdditionalInfoDialog>
												)}
											</Dropdown.Menu>
										</Dropdown>
									</Box>
								) : null,
							]
						})}
					/>
				)}
			</CursorListView>
		</Search>
	)
}

function Filters() {
	return (
		<Stack gap="4">
			<DateRangePickerField
				fromName="created_after"
				toName="created_before"
				label="Created Between"
				dateFormat="DD MMM, YYYY"
			/>
			<SelectField
				select={SelectLocations}
				name="locations"
				multiple
				label="Cities/Locations"
				creatable={false}
				addressableType="tourists"
			/>
		</Stack>
	)
}

export interface SelectTouristsAndGroupsProps extends Omit<
	AsyncSelectProps,
	"fetch"
> {
	tripId: number
}

export function SelectTouristsAndGroups({
	tripId,
	...props
}: SelectTouristsAndGroupsProps) {
	const xhr = useXHR()

	return (
		<AsyncSelect
			{...props}
			searchable
			multiple
			fetchOnMount
			placeholder="Select tourists or groups..."
			optionRenderer={({ option }: { option: ITouristOrGroupOption }) => {
				const isTourist = option.member_type === "tourists"
				const isChild =
					isTourist &&
					(option as ITouristOrGroupOption<TTourist>)?.member?.type === "child"
				const childAge = isChild
					? (option as ITouristOrGroupOption<TTourist>)?.member?.age_during_trip
					: null
				return (
					<Box>
						<Inline alignItems="center" gap="2">
							<Box>
								{isChild ? (
									<Icons.Child />
								) : isTourist ? (
									<Icons.User />
								) : (
									<Icons.UserGroup />
								)}
							</Box>
							<Box>
								{option?.name}
								{isChild ? ` (${childAge}y)` : ``}
							</Box>
						</Inline>
					</Box>
				)
			}}
			fetch={async (q) => {
				// Get tourist list
				const touristsResp = await xhr.get<ISimpleListResponse<TTourist>>(
					`/tourists`,
					{
						params: {
							trip: tripId,
							limit: 1000,
							q,
						},
					}
				)
				const touristsData = touristsResp.data?.data || []
				const touristsOptions = touristsData.map((t) => ({
					id: `tourist-${t.id}`,
					member_type: "tourists",
					member_id: t.id,
					member: t,
					name: t.name,
				}))

				// Get tourist groups
				const groupsResp = await xhr.get<ISimpleListResponse<TTouristGroup>>(
					`/tourist-groups`,
					{
						params: {
							trip: tripId,
							limit: 1000,
							q,
						},
					}
				)
				const groupsData = groupsResp.data?.data || []
				const groupsOptions = groupsData.map((g) => ({
					id: `group-${g.id}`,
					member_type: "tourist_groups",
					member_id: g.id,
					member: g,
					name: g.name,
				}))

				return [...groupsOptions, ...touristsOptions]
			}}
		/>
	)
}

interface TSelectTouristsInGroup {
	is_primary: boolean
	tourist: TTourist
	name: string
	id: number
	group: TTouristGroup | undefined
}
export function SelectTouristsInGroup({
	tripId,
	group,
	...props
}: Omit<AsyncSelectProps, "fetch"> & {
	tripId: number | string
	group?: TTouristGroup
}) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			{...props}
			multiple
			placeholder="Select tourists..."
			searchable={true}
			optionRenderer={({ option }: { option: TSelectTouristsInGroup }) => {
				const { name, tourist, group } = option
				const { age_during_trip, type, groups } = tourist
				const filteredGroup = groups?.filter((g) => g.id !== group?.id)
				const isGroupsPresent = !!(filteredGroup?.length ?? 0)
				return (
					<Stack gap="2" color={isGroupsPresent ? "muted" : "default"}>
						<Text>
							{type === "adult" ? <Icons.Adult /> : <Icons.Child />} {name}
							{age_during_trip ? ` (${age_during_trip}y)` : ""}
						</Text>
						<Inline gap="2" flexWrap="wrap">
							{filteredGroup?.map((g) => (
								<Badge>
									<Text color={isGroupsPresent ? "muted" : "default"}>
										{g.name}
									</Text>
								</Badge>
							))}
						</Inline>
					</Stack>
				)
			}}
			fetch={async (q) => {
				const resp = await xhr.get<ISimpleListResponse<TTourist>>("/tourists", {
					params: {
						trip: tripId,
						limit: 1000,
						include: "groups",
						q,
					},
				})
				return (
					(resp.data.data || []).map((t) => ({
						is_primary: t.is_primary ? 1 : 0,
						tourist: t,
						name: t.name,
						id: t.id,
						group,
					})) || []
				)
			}}
		/>
	)
}

export function SelectTouristDocumentType({
	tripId,
	tripDocumentsOnly = true,
	...props
}: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch"> & {
	tripId: number
	tripDocumentsOnly?: boolean
}) {
	const xhr = useXHR()
	const [newName, setNewName] = useState("")

	const promiseOfCreation = useRef<
		((documentType: IDocType) => void) | undefined
	>(undefined)
	return (
		<>
			<AsyncSelect
				onCreateNew={(query: string) => {
					setNewName(query)
					return new Promise((resolve) => {
						promiseOfCreation.current = resolve
					})
				}}
				{...props}
				optionRenderer={({ option, created }) => {
					if (created) {
						return <Box>Add "{option.name}"</Box>
					}
					return <Box>{option.name}</Box>
				}}
				fetch={async (q) => {
					const resp = await xhr.get<ISimpleListResponse<IDocType>>(
						"/document-types",
						{
							params: {
								q,
								trip_id: tripId,
								trip_documents_only: tripDocumentsOnly,
							},
						}
					)
					return resp.data.data || []
				}}
			/>
			<AddTouristDocumentDialog
				open={!!newName}
				onClose={() => {
					setNewName("")
				}}
				onSuccess={(documentType) => {
					promiseOfCreation.current?.(documentType)
					setNewName("")
				}}
			/>
		</>
	)
}

export function GuestDetails({
	tourists,
}: {
	tourists: TTourist[] | undefined
}) {
	if (!tourists?.length) return null
	return (
		<Stack gap="2">
			<Text fontWeight="semibold">Guests:</Text>
			<Inline gap="2" flexWrap="wrap">
				{tourists.map((t) => {
					const isChild = t.type === "child"
					const childAge = isChild ? t.age_during_trip : null
					return (
						<Badge
							status={
								isTruthy(t.booking_tourist?.is_primary)
									? "success"
									: t.type === "adult"
										? "primary"
										: "info"
							}
						>
							<Tooltip
								content={
									t.booking_tourist?.remarks
										? t.booking_tourist.remarks
										: "No remarks available"
								}
							>
								<Box key={`tourist_${t.id}`}>
									<Inline alignItems="center" gap="2">
										<Box>{isChild ? <Icons.Child /> : <Icons.User />}</Box>
										<Box>
											{t.name}
											{isTruthy(t.booking_tourist?.is_primary) ? (
												<Box as="span" marginLeft="1">
													<Icons.Star />
												</Box>
											) : null}
											{isChild ? ` (${childAge}y)` : ``}
										</Box>
									</Inline>
								</Box>
							</Tooltip>
						</Badge>
					)
				})}
			</Inline>
		</Stack>
	)
}

export function TouristInfoEmailView({ tourists }: { tourists?: TTourist[] }) {
	if ((tourists || []).length) {
		return (
			<div style={{ paddingTop: "5px" }}>
				{(tourists || []).map((t) => {
					const age = t.type === "child" ? ` (${t.age_during_trip}y)` : ""
					const remark = t.booking_tourist?.remarks

					return (
						<p key={t.id} style={{ marginBottom: "5px" }}>
							<span>
								{t.name}
								{age}
							</span>
							{remark && (
								<p
									style={{
										fontWeight: "bold",
										borderLeft: "2px solid silver",
										paddingLeft: "4px",
									}}
								>
									<small
										style={{
											whiteSpace: "pre-line",
										}}
									>
										{remark}
									</small>
								</p>
							)}
						</p>
					)
				})}
			</div>
		)
	} else return null
}

export function SelectTourist({
	tripId,
	...props
}: Omit<AsyncSelectProps, "fetch"> & {
	tripId: number | string
}) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			{...props}
			placeholder="Type to search..."
			searchable={true}
			optionRenderer={({ option: tourist }: { option: TTourist }) => {
				const { name, age_during_trip, type } = tourist
				return (
					<Stack gap="2" color={"default"}>
						<Text>
							{type === "adult" ? <Icons.Adult /> : <Icons.Child />}
							{name}
							{age_during_trip ? ` (${age_during_trip}y)` : ""}
						</Text>
					</Stack>
				)
			}}
			fetch={async (q) => {
				const resp = await xhr.get<ISimpleListResponse<TTourist>>("/tourists", {
					params: {
						trip: tripId,
						limit: 1000,
						q,
					},
				})
				return resp.data.data || []
			}}
		/>
	)
}
